import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Card, Form, Select, DatePicker, Button, InputNumber, message, Spin, Row, Col, Statistic } from 'antd'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js'
import { fetchForecastData, selectForecast } from '../../store/analyticsSlice'
import apiService from '../../services/api'
import dayjs from 'dayjs'
import { formatChartTooltipValue } from '../../utils/formatters'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
)

const { RangePicker } = DatePicker
const { Option } = Select

const ForecastChart = () => {
  const dispatch = useDispatch()
  const { data, loading, error } = useSelector(selectForecast)
  const [form] = Form.useForm()

  const [exchanges, setExchanges] = useState([])
  const [accounts, setAccounts] = useState([])
  const [loadingOptions, setLoadingOptions] = useState(false)

  useEffect(() => {
    loadOptions()
  }, [])

  const loadOptions = async () => {
    setLoadingOptions(true)
    try {
      const [exchangesRes, accountsRes] = await Promise.all([
        apiService.exchanges.getActiveExchanges(),
        apiService.trades.getActiveAccounts(),
      ])

      setExchanges(exchangesRes.data.data || [])
      setAccounts(accountsRes.data.data || [])
    } catch (err) {
      message.error('加载选项失败')
    } finally {
      setLoadingOptions(false)
    }
  }

  const handleSubmit = async (values) => {
    const params = {
      metric: values.metric,
      // 后端服务目前忽略请求中的日期范围，硬编码为过去30天
      // 但我们可以保留以备后用
      start_date: values.dateRange[0].format('YYYY-MM-DD'),
      end_date: values.dateRange[1].format('YYYY-MM-DD'),
      days: values.forecast_days || 7,
      confidence: 0.95,
    }

    // 设置单一目标
    if (values.exchange) {
      params.target = values.exchange
    } else if (values.account) {
      params.target = values.account
    } else if (values.pair) {
      params.target = values.pair
    } else {
      params.target = "" // 默认为全部
    }

    dispatch(fetchForecastData(params))
  }

  const chartData = data ? {
    labels: [...(data.historical_dates || []), ...(data.forecast_dates || [])],
    datasets: [
      {
        label: '历史数据',
        data: [...(data.historical_values || []), ...Array(data.forecast_values?.length || 0).fill(null)],
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.1)',
        tension: 0.3,
        fill: false,
      },
      {
        label: '预测数据',
        data: [...Array(data.historical_values?.length || 0).fill(null), ...(data.forecast_values || [])],
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.1)',
        borderDash: [5, 5],
        tension: 0.3,
        fill: false,
      },
      {
        label: '置信区间上限',
        data: [...Array(data.historical_values?.length || 0).fill(null), ...(data.upper_bound || [])],
        borderColor: 'rgba(255, 99, 132, 0.3)',
        backgroundColor: 'rgba(255, 99, 132, 0.05)',
        borderDash: [2, 2],
        tension: 0.3,
        fill: '+1',
        pointRadius: 0,
      },
      {
        label: '置信区间下限',
        data: [...Array(data.historical_values?.length || 0).fill(null), ...(data.lower_bound || [])],
        borderColor: 'rgba(255, 99, 132, 0.3)',
        backgroundColor: 'rgba(255, 99, 132, 0.05)',
        borderDash: [2, 2],
        tension: 0.3,
        fill: '-1',
        pointRadius: 0,
      },
    ],
  } : null

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: '趋势预测',
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: (ctx) => {
            const label = ctx.dataset.label ? `${ctx.dataset.label}: ` : ''
            const y = ctx.parsed.y
            return `${label}${formatChartTooltipValue(y)}`
          },
        },
      },
      datalabels: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
  }

  return (
    <div style={{ padding: '24px' }}>
      <Card title="趋势预测" style={{ marginBottom: 24 }}>
        <Form
          form={form}
          layout="inline"
          onFinish={handleSubmit}
          initialValues={{
            metric: 'volume',
            dateRange: [
              dayjs().subtract(30, 'day').startOf('day'),
              dayjs().subtract(1, 'day').endOf('day'),
            ],
            forecast_days: 7,
          }}
        >
          <Form.Item name="metric" label="指标" rules={[{ required: true }]}>
            <Select style={{ width: 150 }}>
              <Option key="volume" value="volume">交易量</Option>
              <Option key="pnl" value="pnl">盈亏</Option>
              <Option key="maker_volume" value="maker_volume">Maker 交易量</Option>
              <Option key="taker_volume" value="taker_volume">Taker 交易量</Option>
            </Select>
          </Form.Item>

          <Form.Item name="dateRange" label="历史数据范围" rules={[{ required: true }]}>
            <RangePicker />
          </Form.Item>

          <Form.Item name="forecast_days" label="预测天数" rules={[{ required: true }]}>
            <InputNumber min={1} max={30} style={{ width: 100 }} />
          </Form.Item>

          <Form.Item name="exchange" label="交易所">
            <Select
              style={{ width: 150 }}
              placeholder="全部"
              loading={loadingOptions}
              allowClear
            >
              {exchanges.map(ex => (
                <Option key={ex.code} value={ex.code}>{ex.name}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="account" label="账户">
            <Select
              style={{ width: 150 }}
              placeholder="全部"
              loading={loadingOptions}
              allowClear
            >
              {accounts.map(acc => (
                <Option key={`${acc.account}_${acc.exchange}_${acc.trade_type}`} value={acc.account}>{acc.account}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              预测
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {error && (
        <Card style={{ marginBottom: 24 }}>
          <div style={{ color: 'red' }}>错误: {error}</div>
        </Card>
      )}

      {loading && (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
          </div>
        </Card>
      )}

      {data && !loading && (
        <>
          <Row gutter={24} style={{ marginBottom: 24 }}>
            <Col span={6}>
              <Card>
                <Statistic
                  title="R² 分数"
                  value={data.r2_score || 0}
                  precision={4}
                  valueStyle={{ color: data.r2_score > 0.7 ? '#3f8600' : '#cf1322' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="平均绝对误差"
                  value={data.mae || 0}
                  precision={2}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="均方根误差"
                  value={data.rmse || 0}
                  precision={2}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="预测天数"
                  value={data.forecast_days || 0}
                  suffix="天"
                />
              </Card>
            </Col>
          </Row>

          <Card title="预测图表">
            <div style={{ height: 500 }}>
              {chartData && <Line data={chartData} options={chartOptions} />}
            </div>
          </Card>

          {data.model_info && (
            <Card title="模型信息" style={{ marginTop: 24 }}>
              <Row gutter={16}>
                <Col span={8}>
                  <p><strong>模型类型:</strong> {data.model_info.type || '线性回归'}</p>
                </Col>
                <Col span={8}>
                  <p><strong>训练样本数:</strong> {data.model_info.train_samples || 0}</p>
                </Col>
                <Col span={8}>
                  <p><strong>特征数:</strong> {data.model_info.features || 0}</p>
                </Col>
              </Row>
            </Card>
          )}
        </>
      )}
    </div>
  )
}

export default ForecastChart